import React from 'react'
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { MultiTableConfigTable } from '../../../types/gotypes'
import TableSourcePicker from '../widgets/genai/TableSourcePicker'

export interface AgentCreatePanelProps {
  customer: string
  job: string
  name: string
  onNameChange: (name: string) => void
  description: string
  onDescriptionChange: (description: string) => void
  selectedTables: MultiTableConfigTable[]
  onSelectedTablesChange: (tables: MultiTableConfigTable[]) => void
  isGenerating: boolean
  error: string | null
  onGenerate: () => void
  /** Why this person may not generate (#1537 AI access), or undefined. */
  blockedReason?: string
  /** Takes the person to "Your AI access" to ask for a higher level (#1537 PR D). Only rendered alongside blockedReason. */
  onRequestAccess?: () => void
}

/**
 * The "Let the agent help" describe step (#971 Phase 3). Pure presentation —
 * the generate call lives in DashboardCreatePage via
 * useGenerateDashboardStructure (Page → Hook → Component). A failed
 * generation renders inline with every input preserved and the button
 * becoming "Try again" — retryable, never a dead end.
 */
const AgentCreatePanel: React.FC<AgentCreatePanelProps> = ({
  customer,
  job,
  name,
  onNameChange,
  description,
  onDescriptionChange,
  selectedTables,
  onSelectedTablesChange,
  isGenerating,
  error,
  onGenerate,
  blockedReason,
  onRequestAccess,
}) => {
  const canGenerate = Boolean(name.trim() && description.trim()) && !isGenerating && !blockedReason

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Let the agent help</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Describe the dashboard you want. The agent drafts the structure — pages, charts, Gen AI
        insight cards — and you refine it in the layout builder before anything is created.
      </Typography>

      <TextField
        autoFocus
        fullWidth
        label="Dashboard name"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        sx={{ mb: 2 }}
        disabled={isGenerating}
      />

      <TextField
        fullWidth
        multiline
        minRows={4}
        label="Describe what you want"
        placeholder={'e.g. "A 3-tab dashboard — Sky TV, Sky Mobile, Sky Broadband. Each tab has a line chart showing the brand funnel and three Gen AI text boxes underneath."'}
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
        sx={{ mb: 3 }}
        disabled={isGenerating}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Reference tables (optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Pick tables for the agent to wire into charts and reference in Gen AI instructions. It will
        never use tables you didn&apos;t select here.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <TableSourcePicker
          customer={customer}
          job={job}
          selected={selectedTables}
          onSelectedChange={onSelectedTablesChange}
          height={220}
        />
      </Box>

      {blockedReason && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={onRequestAccess && (
            <Button color="inherit" size="small" onClick={onRequestAccess}>Request access</Button>
          )}
        >
          {blockedReason}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        color="info"
        onClick={onGenerate}
        disabled={!canGenerate}
        startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
      >
        {isGenerating ? 'Generating structure…' : error ? 'Try again' : 'Generate structure'}
      </Button>
    </Box>
  )
}

export default AgentCreatePanel
